const REPORT_MONTH_STORAGE_KEY = "cfo.reportMonth";
const REPORT_START_MONTH_STORAGE_KEY = "cfo.reportStartMonth";
const REPORT_END_MONTH_STORAGE_KEY = "cfo.reportEndMonth";
const REPORT_INFO_STORAGE_KEY = "cfo.reportInfo";
const REPORT_CACHE_CLEARED_SESSION_KEY = "cfo.reportCacheCleared";

const isValidMonthString = (value: string): boolean =>
  /^\d{4}-\d{2}$/.test(value);

/** "2025-03" → "March 2025" (aligns with balance sheet summary API `as_of.label`). */
export const formatYearMonthLongLabel = (yearMonth: string): string => {
  if (!isValidMonthString(yearMonth)) {
    return "";
  }
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

export const getDefaultReportMonth = (): string => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export const getStoredReportMonth = (): string | null => {
  try {
    const stored = window.localStorage.getItem(REPORT_MONTH_STORAGE_KEY);
    if (stored && isValidMonthString(stored)) {
      return stored;
    }
  } catch (error) {
    return null;
  }
  return null;
};

export const getInitialReportMonth = (): string =>
  getStoredReportMonth() ?? getDefaultReportMonth();

export const setStoredReportMonth = (month: string): void => {
  if (!isValidMonthString(month)) {
    return;
  }
  try {
    window.localStorage.setItem(REPORT_MONTH_STORAGE_KEY, month);
  } catch (error) {
    return;
  }
};

/**
 * Calculate default start and end months based on effective date
 * End month = effective date month
 * Start month = end month - 6 months
 * @param effectiveYear Year from effective date
 * @param effectiveMonth Month from effective date (1-12)
 * @returns Object with startMonth and endMonth in YYYY-MM format
 */
export const calculateDefaultMonths = (
  effectiveYear: number,
  effectiveMonth: number,
): { startMonth: string; endMonth: string } => {
  // End month is the effective date month
  const endMonth = `${effectiveYear}-${String(effectiveMonth).padStart(2, "0")}`;

  // Start month is 6 months before end month
  const startDate = new Date(effectiveYear, effectiveMonth - 1, 1);
  startDate.setMonth(startDate.getMonth() - 6);
  const startMonth = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}`;

  return { startMonth, endMonth };
};

/**
 * Get stored start and end months from localStorage
 * @returns Object with startMonth and endMonth or null if not stored
 */
export const getStoredReportMonths = (): {
  startMonth: string;
  endMonth: string;
} | null => {
  try {
    const startMonth = window.localStorage.getItem(
      REPORT_START_MONTH_STORAGE_KEY,
    );
    const endMonth = window.localStorage.getItem(REPORT_END_MONTH_STORAGE_KEY);

    if (
      startMonth &&
      endMonth &&
      isValidMonthString(startMonth) &&
      isValidMonthString(endMonth)
    ) {
      return { startMonth, endMonth };
    }
  } catch (error) {
    return null;
  }
  return null;
};

/**
 * Store start and end months in localStorage
 * @param startMonth Start month in YYYY-MM format
 * @param endMonth End month in YYYY-MM format
 */
export const setStoredReportMonths = (
  startMonth: string,
  endMonth: string,
): void => {
  if (!isValidMonthString(startMonth) || !isValidMonthString(endMonth)) {
    return;
  }
  try {
    window.localStorage.setItem(REPORT_START_MONTH_STORAGE_KEY, startMonth);
    window.localStorage.setItem(REPORT_END_MONTH_STORAGE_KEY, endMonth);
  } catch (error) {
    return;
  }
};

export interface StoredReportInfo {
  startMonth: string;
  endMonth: string;
  reportId: number;
  reportName: string;
}

/**
 * Get stored report info from localStorage
 * @returns StoredReportInfo or null if missing/invalid
 */
export const getStoredReportInfo = (): StoredReportInfo | null => {
  try {
    const raw = window.localStorage.getItem(REPORT_INFO_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredReportInfo>;
    if (
      typeof parsed.reportId === "number" &&
      typeof parsed.reportName === "string" &&
      typeof parsed.startMonth === "string" &&
      typeof parsed.endMonth === "string" &&
      isValidMonthString(parsed.startMonth) &&
      isValidMonthString(parsed.endMonth)
    ) {
      return {
        reportId: parsed.reportId,
        reportName: parsed.reportName,
        startMonth: parsed.startMonth,
        endMonth: parsed.endMonth,
      };
    }
  } catch (error) {
    return null;
  }
  return null;
};

/**
 * Store report info in localStorage
 */
export const setStoredReportInfo = (info: StoredReportInfo): void => {
  if (
    !isValidMonthString(info.startMonth) ||
    !isValidMonthString(info.endMonth) ||
    typeof info.reportId !== "number"
  ) {
    return;
  }
  try {
    window.localStorage.setItem(REPORT_INFO_STORAGE_KEY, JSON.stringify(info));
  } catch (error) {
    return;
  }
};

export const clearStoredReportInfo = (): void => {
  try {
    window.localStorage.removeItem(REPORT_INFO_STORAGE_KEY);
  } catch (error) {
    return;
  }
};

export const shouldClearReportCacheOnLoad = (): boolean => {
  try {
    const alreadyCleared = window.sessionStorage.getItem(
      REPORT_CACHE_CLEARED_SESSION_KEY,
    );
    if (alreadyCleared) {
      return false;
    }
    window.sessionStorage.setItem(REPORT_CACHE_CLEARED_SESSION_KEY, "true");
    return true;
  } catch (error) {
    return false;
  }
};
